import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, Trash2, ArrowLeft, ArrowRight, Film } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { fileUrl } from "./HeroSlides";
import { FocusPicker, focusPos } from "./FocusPicker";
import { TrimEditor } from "./VideoTrim";

const SECTIONS = [["social", "Wedding feed (TikTok-style posts, go live)"], ["schedule", "Schedule of events"], ["games", "Games & party fun"], ["party", "Meet the wedding party"], ["albums", "Photo albums"]];

export function BannerManager({ w, onChange }) {
  const [busy, setBusy] = useState(false);
  const [sel, setSel] = useState(null);
  const ref = useRef();
  const picked = (w.banner_slides || []).find((s) => s.slide_id === sel && s.type === "image");
  const pickedVideo = (w.banner_slides || []).find((s) => s.slide_id === sel && s.type === "video");
  const slides = w.banner_slides || [];
  const run = async (fn, ok) => { try { const { data } = await fn(); onChange({ banner_slides: data }); if (ok) toast.success(ok); } catch (e) { toast.error(formatApiError(e)); } };
  async function add(e) {
    const picked = Array.from(e.target.files || []);
    const files = picked.slice(0, 6 - slides.length);
    e.target.value = "";
    if (picked.length > files.length) toast.message(`Only ${files.length} more slide${files.length === 1 ? "" : "s"} fit (6 max). The rest were skipped.`);
    setBusy(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      await run(() => api.post("/weddings/mine/banner", fd), "Added to your banner");
    }
    setBusy(false);
  }
  return (
    <div className="space-y-3" data-testid="banner-manager">
      <p className="text-sm font-medium">Banner slideshow <span className="text-xs text-[var(--muted)]">· up to 6 photos or videos</span></p>
      <div className="flex flex-wrap gap-2">
        {slides.map((s, i) => (
          <div key={s.slide_id} className="relative w-28 h-20 rounded-xl overflow-hidden bg-black group" data-testid={`banner-slide-${i}`}>
            {s.type === "video" ? <video src={fileUrl(s.path)} muted onClick={() => setSel(sel === s.slide_id ? null : s.slide_id)} className={`w-full h-full object-cover cursor-pointer ${sel === s.slide_id ? "ring-2 ring-[var(--gold)]" : ""}`} data-testid={`banner-trim-open-${i}`} /> : <img src={fileUrl(s.path)} alt="" onClick={() => setSel(sel === s.slide_id ? null : s.slide_id)} className={`w-full h-full object-cover cursor-pointer ${sel === s.slide_id ? "ring-2 ring-[var(--gold)]" : ""}`} style={{ objectPosition: focusPos(s.focus) }} data-testid={`banner-focus-open-${i}`} />}
            {s.type === "video" && <Film className="absolute top-1 left-1 w-4 h-4 text-white" />}
            <div className="absolute inset-x-0 bottom-0 flex justify-between p-1 bg-black/50 opacity-100 md:opacity-0 group-hover:opacity-100 transition-opacity">
              <button disabled={i === 0} onClick={() => run(() => api.post("/weddings/mine/banner/move", { from_index: i, to_index: i - 1 }))} className="text-white disabled:opacity-30" data-testid={`banner-left-${i}`}><ArrowLeft className="w-3.5 h-3.5" /></button>
              <button onClick={() => run(() => api.delete(`/weddings/mine/banner/${s.slide_id}`), "Removed")} className="text-white" data-testid={`banner-remove-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
              <button disabled={i === slides.length - 1} onClick={() => run(() => api.post("/weddings/mine/banner/move", { from_index: i, to_index: i + 1 }))} className="text-white disabled:opacity-30" data-testid={`banner-right-${i}`}><ArrowRight className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        ))}
        {slides.length < 6 && <button onClick={() => ref.current?.click()} disabled={busy} className="w-28 h-20 rounded-xl border-2 border-dashed border-[var(--line)] flex flex-col items-center justify-center text-xs text-[var(--muted)] hover:border-[var(--gold)]" data-testid="banner-add">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <><ImagePlus className="w-5 h-5" />Photo / video</>}</button>}
      </div>
      <input ref={ref} type="file" accept="image/*,video/mp4,video/webm,video/quicktime" multiple onChange={add} className="hidden" data-testid="banner-input" />
      {slides.length > 0 && !picked && !pickedVideo && <p className="text-xs text-[var(--muted)]">Tap a photo to choose which part stays visible, or tap a video to pick the seconds that loop.</p>}
      {pickedVideo && (
        <div className="rounded-2xl border border-[var(--line)] p-4 bg-white">
          <TrimEditor key={pickedVideo.slide_id} slide={pickedVideo} onSaved={(data) => onChange({ banner_slides: data })} />
          <button onClick={() => setSel(null)} className="text-xs underline mt-3" data-testid="banner-trim-done">Done</button>
        </div>
      )}
      {picked && (
        <div className="rounded-2xl border border-[var(--line)] p-4 bg-white">
          <FocusPicker src={fileUrl(picked.path)} focus={picked.focus} testid="banner-focus"
            onChange={(f) => { onChange({ banner_slides: slides.map((s) => (s.slide_id === picked.slide_id ? { ...s, focus: f } : s)) }); api.put(`/weddings/mine/banner/${picked.slide_id}/focus`, f).catch((e) => toast.error(formatApiError(e))); }} />
          <button onClick={() => setSel(null)} className="text-xs underline mt-3" data-testid="banner-focus-done">Done</button>
        </div>
      )}
    </div>
  );
}

export function SiteSectionsCard({ w, onChange }) {
  const cur = w.site_sections || {};
  async function save(patch) {
    try { const { data } = await api.put("/weddings/mine/site-settings", patch); onChange(data); toast.success("Saved"); } catch (e) { toast.error(formatApiError(e)); }
  }
  return (
    <div className="rounded-2xl bg-[var(--bg-warm)] p-4 space-y-2" data-testid="site-sections">
      <p className="text-sm font-medium">Guest sections on your site</p>
      {SECTIONS.map(([k, l]) => (
        <label key={k} className="flex items-center gap-2 text-sm"><input type="checkbox" checked={cur[k] !== false} onChange={(e) => save({ site_sections: { ...cur, [k]: e.target.checked } })} data-testid={`section-toggle-${k}`} /> {l}</label>
      ))}
      <label className="flex items-center gap-2 text-sm pt-2 border-t border-[var(--line)]"><input type="checkbox" checked={w.auto_approve_guest_posts !== false} onChange={(e) => save({ auto_approve_guest_posts: e.target.checked })} data-testid="auto-approve-toggle" /> Guest posts appear right away (untick to approve each one first)</label>
    </div>
  );
}
